import React from "react";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  Input,
  FormErrorMessage,
  SimpleGrid,
  Box,
  FormLabel,
  InputGroup,
  InputLeftAddon,
  Icon,
  Heading,
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import validator from "validator";
import { Globe, Mail, Phone } from "react-feather";
import HelpTooltip from "../common/HelpTooltip";

type Inputs = {
  name: string;
  url: string;
  officeEmail: string;
  phone: string;
  position: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
};

type Props = {
  panel?: boolean;
  isSuperadmin?: boolean;
};

const PressOfficeForm = ({
  panel = false,
  isSuperadmin = false,
}: Props): React.ReactElement => {
  const {
    formState: { errors },
    register,
  } = useFormContext<Inputs>();
  const { t } = useTranslation("common");

  return (
    <>
      {!panel && (
        <Heading as="h3" size="lg">
          {t("pressOffice.fillOfficeData")}
        </Heading>
      )}
      <SimpleGrid
        columns={{ base: 1, md: panel ? 2 : 1 }}
        spacingX={panel ? 5 : 0}
        spacingY={panel ? 3 : "15px"}
      >
        <FormControl isInvalid={!!errors.name} isRequired>
          <Box>
            {panel && <FormLabel htmlFor="name">{t("name")}</FormLabel>}
            <Input
              autoFocus
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("name")}
              type="text"
              {...register("name", { required: `${t("users.fieldRequired")}` })}
            />
            {errors.name && (
              <FormErrorMessage>{errors.name.message}</FormErrorMessage>
            )}
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="url">{t("users.url")}</FormLabel>}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={Globe} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("users.url")}
                type="text"
                {...register("url")}
              />
            </InputGroup>
          </Box>
        </FormControl>
        <FormControl isInvalid={!!errors.officeEmail} isRequired>
          <Box>
            {panel && (
              <FormLabel htmlFor="officeEmail">
                {t("users.email")} <HelpTooltip label="settings.pressEmail" />
              </FormLabel>
            )}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={Mail} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("users.email")}
                type="email"
                {...register("officeEmail", {
                  required: `${t("users.fieldRequired")}`,
                  validate: (value) =>
                    !value || validator.isEmail(value)
                      ? true
                      : `${t("users.emailValidationError")}`,
                })}
              />
            </InputGroup>
            {errors.officeEmail && (
              <FormErrorMessage>{errors.officeEmail.message}</FormErrorMessage>
            )}
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="phone">{t("users.phone")}</FormLabel>}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={Phone} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("users.phone")}
                type="text"
                {...register("phone")}
              />
            </InputGroup>
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="address">{t("address")}</FormLabel>}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("address")}
              type="text"
              {...register("address")}
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="city">{t("users.city")}</FormLabel>}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("users.city")}
              type="text"
              {...register("city")}
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && (
              <FormLabel htmlFor="postalCode">
                {t("users.postalCode")}
              </FormLabel>
            )}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("users.postalCode")}
              type="text"
              {...register("postalCode")}
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && (
              <FormLabel htmlFor="country">{t("users.country")}</FormLabel>
            )}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("users.country")}
              type="text"
              {...register("country")}
            />
          </Box>
        </FormControl>
      </SimpleGrid>
    </>
  );
};

export default PressOfficeForm;
